// frontend/src/components/Admin/AdminDossierDetails.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Badge, Button, Alert, Form, Row, Col, Table, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaTimes, FaEye, FaInfoCircle } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';

const AdminDossierDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [dossier, setDossier] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [preferences, setPreferences] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showActionModal, setShowActionModal] = useState(false);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);
    const [loadingPreferences, setLoadingPreferences] = useState(false);
    const [actionType, setActionType] = useState('');
    const [comment, setComment] = useState('');
    const [processing, setProcessing] = useState(false);

    const loadDossier = useCallback(async () => {
        try {
            const response = await api.get(`/dossiers/${id}/`);
            setDossier(response.data);
            setDocuments(response.data.documents || []);
        } catch (error) {
            console.error('Error loading dossier:', error);
            toast.error('Erreur lors du chargement du dossier');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadDossier();
    }, [loadDossier]);

    const loadPreferences = async () => {
        if (!dossier) return;
        
        setLoadingPreferences(true);
        try {
            const endpoint = dossier.visa_type === 'study'
                ? `/dossiers/${id}/study-preferences/`
                : `/dossiers/${id}/work-preferences/`;
            
            const response = await api.get(endpoint);
            setPreferences(response.data.preferences);
            setShowPreferencesModal(true);
        } catch (error) {
            if (error.response?.status === 404) {
                toast.warning('⚠️ Aucune préférence enregistrée pour ce dossier');
            } else {
                console.error('Erreur chargement préférences:', error);
                toast.error('Erreur lors du chargement des préférences');
            }
        } finally {
            setLoadingPreferences(false);
        }
    };

    const handleAction = async () => {
        if (!comment && actionType === 'reject') {
            toast.error('Veuillez fournir un motif de rejet');
            return;
        }

        setProcessing(true);
        try {
            const newStatus = actionType === 'approve' ? 'approved' : 'rejected';
            await api.patch(`/dossiers/${id}/`, {
                status: newStatus,
                admin_comment: comment
            });

            toast.success(
                actionType === 'approve' 
                    ? '✅ Dossier approuvé avec succès' 
                    : '❌ Dossier rejeté'
            );
            
            setShowActionModal(false);
            setComment('');
            loadDossier();
        } catch (error) {
            console.error('Error updating dossier:', error);
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setProcessing(false);
        }
    };

    const handleViewDocument = (doc) => {
        const url = `${api.defaults.baseURL}/documents/${doc.id}?token=${localStorage.getItem('access_token')}`;
        window.open(url, '_blank');
    };

    const getStatusBadge = (status) => {
        const variants = {
            draft: 'secondary',
            submitted: 'info',
            in_review: 'warning',
            approved: 'success',
            rejected: 'danger',
        };
        
        const labels = {
            draft: 'Brouillon',
            submitted: 'Soumis',
            in_review: 'En révision',
            approved: 'Approuvé',
            rejected: 'Rejeté',
        };

        return <Badge bg={variants[status]}>{labels[status]}</Badge>;
    };

    const getVisaTypeLabel = (type) => {
        const types = {
            work: 'Visa de Travail',
            study: 'Visa d\'Études',
            tourist: 'Visa Touristique',
            family: 'Regroupement Familial',
            business: 'Visa d\'Affaires',
        };
        return types[type] || type;
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <div className="spinner-border text-primary"></div>
                <p className="mt-3">Chargement...</p>
            </Container>
        );
    }

    if (!dossier) {
        return (
            <Container className="py-5">
                <Alert variant="danger">Dossier non trouvé</Alert>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            {/* En-tête */}
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col>
                            <h2 className="mb-2">Dossier #{dossier.id}</h2>
                            <div className="d-flex gap-3 align-items-center">
                                {getStatusBadge(dossier.status)}
                                <Badge bg="primary">{getVisaTypeLabel(dossier.visa_type)}</Badge>
                            </div>
                        </Col>
                        <Col xs="auto">
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/dossiers')}>
                                <FaArrowLeft className="me-2" />
                                Retour
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Informations utilisateur */}
            <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Informations du demandeur</h5>
                    {(dossier.visa_type === 'study' || dossier.visa_type === 'work') && (
                        <Button 
                            variant="outline-info" 
                            size="sm"
                            onClick={loadPreferences}
                            disabled={loadingPreferences}
                        >
                            {loadingPreferences ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Chargement...
                                </>
                            ) : (
                                <>
                                    <FaInfoCircle className="me-2" />
                                    Voir les préférences
                                </>
                            )}
                        </Button>
                    )}
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <p><strong>Nom complet :</strong> {dossier.user ? `${dossier.user.first_name} ${dossier.user.last_name}` : 'N/A'}</p>
                            <p><strong>Email :</strong> {dossier.user?.email || 'N/A'}</p>
                        </Col>
                        <Col md={6}>
                            <p><strong>Date de création :</strong> {new Date(dossier.created_at).toLocaleDateString('fr-FR')}</p>
                            <p><strong>Dernière modification :</strong> {new Date(dossier.updated_at).toLocaleDateString('fr-FR')}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Documents */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Documents ({documents.length})</h5>
                </Card.Header>
                <Card.Body>
                    {documents.length === 0 ? (
                        <Alert variant="info">Aucun document téléversé</Alert>
                    ) : (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Date d'ajout</th>
                                    <th>Statut</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {documents.map((doc) => (
                                    <tr key={doc.id}>
                                        <td>{doc.doc_type}</td>
                                        <td>{new Date(doc.created_at).toLocaleDateString('fr-FR')}</td>
                                        <td>
                                            {doc.verified ? (
                                                <Badge bg="success">Vérifié</Badge>
                                            ) : (
                                                <Badge bg="secondary">En attente</Badge>
                                            )}
                                        </td>
                                        <td>
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm"
                                                onClick={() => handleViewDocument(doc)}
                                            >
                                                <FaEye className="me-1" />
                                                Voir
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Reçu de paiement */}
            {(dossier.visa_type === 'study' || dossier.visa_type === 'work' || dossier.payment_required) && (
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">Reçu de paiement</h5>
                    </Card.Header>
                    <Card.Body>
                        {dossier.payment_receipt ? (
                            <Alert variant="success">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>✅ Reçu de paiement téléversé</strong>
                                        <br />
                                        <small className="text-muted">
                                            Date de paiement : {dossier.payment_date
                                                ? new Date(dossier.payment_date).toLocaleDateString('fr-FR')
                                                : 'Date inconnue'}
                                        </small>
                                    </div>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => {
                                            const url = `${api.defaults.baseURL}/dossiers/${dossier.id}/payment-receipt?token=${localStorage.getItem('access_token')}`;
                                            window.open(url, '_blank');
                                        }}
                                    >
                                        <FaEye className="me-1" />
                                        Voir le reçu
                                    </Button>
                                </div>
                            </Alert>
                        ) : (
                            <Alert variant="warning">
                                <strong>⚠️ Aucun reçu de paiement</strong>
                                <br />
                                Le demandeur n'a pas encore téléversé son reçu de paiement.
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Commentaires admin */}
            {dossier.admin_comment && (
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">Commentaires de l'administrateur</h5>
                    </Card.Header>
                    <Card.Body>
                        <p className="mb-0">{dossier.admin_comment}</p>
                    </Card.Body>
                </Card>
            )}

            {/* Actions admin */}
            {(dossier.status === 'submitted' || dossier.status === 'in_review') && (
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">Actions administrateur</h5>
                    </Card.Header>
                    <Card.Body>
                        <div className="d-flex gap-3">
                            <Button 
                                variant="success"
                                onClick={() => {
                                    setActionType('approve');
                                    setShowActionModal(true);
                                }}
                            >
                                <FaCheck className="me-2" />
                                Approuver
                            </Button>
                            <Button 
                                variant="danger"
                                onClick={() => {
                                    setActionType('reject');
                                    setShowActionModal(true);
                                }}
                            >
                                <FaTimes className="me-2" />
                                Rejeter
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Modal des préférences */}
            <Modal 
                show={showPreferencesModal} 
                onHide={() => setShowPreferencesModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {dossier.visa_type === 'study' ? 'Préférences d\'études' : 'Préférences de travail'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {preferences ? (
                        dossier.visa_type === 'study' ? (
                            // Préférences Study
                            <div>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Niveau d'études</strong>
                                            <span className="fs-5">{preferences.study_level}</span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Domaine d'études</strong>
                                            <span className="fs-5">{preferences.field_of_study}</span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Université préférée</strong>
                                            <span className="fs-5">{preferences.preferred_university || 'Non spécifié'}</span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Ville préférée</strong>
                                            <span className="fs-5">{preferences.preferred_city || 'Non spécifié'}</span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Date de début</strong>
                                            <span className="fs-5">
                                                {preferences.start_date 
                                                    ? new Date(preferences.start_date).toLocaleDateString('fr-FR') 
                                                    : 'Non spécifié'}
                                            </span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Durée (mois)</strong>
                                            <span className="fs-5">{preferences.duration_months} mois</span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Lettre d'acceptation</strong>
                                            <span className="fs-5">
                                                <Badge bg={preferences.has_acceptance_letter ? 'success' : 'secondary'}>
                                                    {preferences.has_acceptance_letter ? 'Oui' : 'Non'}
                                                </Badge>
                                            </span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Source de financement</strong>
                                            <span className="fs-5">{preferences.funding_source || 'Non spécifié'}</span>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        ) : (
                            // Préférences Work
                            <div>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Secteur préféré</strong>
                                            <span className="fs-5">{preferences.preferred_sector || 'Non spécifié'}</span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Poste recherché</strong>
                                            <span className="fs-5">{preferences.job_title_sought || 'Non spécifié'}</span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Ville préférée</strong>
                                            <span className="fs-5">{preferences.preferred_city || 'Non spécifié'}</span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Salaire minimum</strong>
                                            <span className="fs-5">
                                                {preferences.minimum_salary ? `${parseFloat(preferences.minimum_salary).toLocaleString('fr-FR')} CAD` : 'Non spécifié'}
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Années d'expérience</strong>
                                            <span className="fs-5">{preferences.years_experience || 0} ans</span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Disponible à partir de</strong>
                                            <span className="fs-5">
                                                {preferences.available_from 
                                                    ? new Date(preferences.available_from).toLocaleDateString('fr-FR') 
                                                    : 'Non spécifié'}
                                            </span>
                                        </div>
                                        <div className="mb-3">
                                            <strong className="text-muted d-block">Prêt à déménager</strong>
                                            <span className="fs-5">
                                                <Badge bg={preferences.willing_to_relocate ? 'success' : 'secondary'}>
                                                    {preferences.willing_to_relocate ? 'Oui' : 'Non'}
                                                </Badge>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                                {preferences.key_skills && (
                                    <div className="mt-3 p-3 bg-light rounded">
                                        <strong className="d-block mb-2">Compétences clés :</strong>
                                        <p className="mb-0">{preferences.key_skills}</p>
                                    </div>
                                )}
                                {preferences.certifications && (
                                    <div className="mt-3 p-3 bg-light rounded">
                                        <strong className="d-block mb-2">Certifications :</strong>
                                        <p className="mb-0">{preferences.certifications}</p>
                                    </div>
                                )}
                                {preferences.additional_notes && (
                                    <div className="mt-3 p-3 bg-light rounded">
                                        <strong className="d-block mb-2">Notes supplémentaires :</strong>
                                        <p className="mb-0">{preferences.additional_notes}</p>
                                    </div>
                                )}
                            </div>
                        )
                    ) : (
                        <Alert variant="info">Aucune préférence enregistrée</Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPreferencesModal(false)}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal d'action */}
            <Modal show={showActionModal} onHide={() => setShowActionModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {actionType === 'approve' ? 'Approuver le dossier' : 'Rejeter le dossier'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>
                            {actionType === 'approve' ? 'Commentaire (optionnel)' : 'Motif de rejet *'}
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={
                                actionType === 'approve' 
                                    ? 'Ajoutez un commentaire...'
                                    : 'Expliquez le motif du rejet...'
                            }
                            required={actionType === 'reject'}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowActionModal(false)}>
                        Annuler
                    </Button>
                    <Button 
                        variant={actionType === 'approve' ? 'success' : 'danger'}
                        onClick={handleAction}
                        disabled={processing}
                    >
                        {processing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Traitement...
                            </>
                        ) : (
                            actionType === 'approve' ? 'Approuver' : 'Rejeter'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminDossierDetails;